import Controller from '@ember/controller';
import { action, set } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import type { ControllerQueryParam } from '@ember/controller';
import type IntlService from 'ember-intl/services/intl';
import type AditoolsAnalytics from '@ebiz/ember-aditools-analytics/services/aditools-analytics';
import type AditoolsSite from '@ebiz/ember-aditools-site/services/aditools-site';
import type CircuitService from '../services/circuit';
import type LoggingService from '../services/logger';
import type FeedbackService from '../services/feedback';
import type { IndexRouteModel } from '../routes/index';

const K_OUTPUT_TAB = 0,
  K_TABLE_TAB = 1,
  K_DEBUG_TAB = 2;

const TAB_NAME_LIST = ['OutputSignal', 'SpurTable', 'Debug'];

const queryParamList: ControllerQueryParam[] = [
  { partId: 'part' },
  { qfin: 'fin' },
  { qsclock: 'sclock' },
  { qmult: 'mult' },
  { qftw: 'ftw' },
  { qtof: 'tof' },
  { qrso: 'rso' },
  { harmonicDB: 'harmonicDB' },
  { pptDB: 'pptDB' },
  { useFilters: 'useFilters' },
  { qfType: 'fType' },
  { qfTop: 'fTop' },
  { qfOrder: 'fOrder' },
  { qfc: 'fc' },
  { qfRipple: 'fRipple' },
  { qfAtten: 'fAtten' },
  { qdebug: 'debug' },
  { lang: 'en' },
];

const delay = function (t: number, v?: object) {
  return new Promise(function (resolve) {
    setTimeout(resolve.bind(null, v), t);
  });
};

interface SystemDialogOptions {
  message: string | ReturnType<typeof htmlSafe>;
  enabled: boolean;
}
interface NotificationDialogOptions {
  title: string | ReturnType<typeof htmlSafe>;
  message: string | ReturnType<typeof htmlSafe>;
  enabled: boolean;
}

interface FeedbackModel {
  name: string;
  email: string;
  feedback: string;
}

const widthTracker = modifier((element, [getDimensions]) => {
  const getDims = getDimensions as (width: number, height: number) => void;
  const resizer = () => {
    const rect = element.getBoundingClientRect();
    getDims(rect.width, rect.height);
  };
  resizer(); // On first priming, make sure it is off the item
  window.addEventListener('resize', resizer);
  return () => {
    window.removeEventListener('resize', resizer);
  };
});

export default class IndexController extends Controller {
  @service('aditools-analytics') declare analytics: AditoolsAnalytics;
  @service('aditools-site') declare siteconfig: AditoolsSite;
  @service declare intl: IntlService;
  @service declare feedback: FeedbackService;
  @service declare logger: LoggingService;
  @service declare circuit: CircuitService;
  declare model: IndexRouteModel;

  queryParams = queryParamList;
  partId = null;
  qfin = null;
  qsclock = null;
  qmult = null;
  qftw = null;
  qtof = null;
  qrso = null;
  harmonicDB = null;
  qpptDB = null;
  useFilters = null;
  qfType = null;
  qfTop = null;
  qfOrder = null;
  qfc = null;
  qfRipple = null;
  qfAtten = null;
  qdebug = null;
  lang = null;
  currentLoadedUrl = '';
  selectedTab = K_OUTPUT_TAB;

  get isShowDebug() {
    return this.qdebug === true || this.qdebug === 'true';
  }
  selectedProductChanged = false;
  enabledTabs = TAB_NAME_LIST.map((item) => {
    return item;
  });

  activeTab = TAB_NAME_LIST[this.selectedTab] as string;
  systemDialog: SystemDialogOptions = { message: '', enabled: false };
  notificationDialog: NotificationDialogOptions = { title: '', message: '', enabled: false };
  siteUrl?: string;
  consentTitle = '';
  consentIsReady = false;
  hideCloseX = true;
  hideTitlebar = true;
  precisionStudioMenuLinks?: Record<string, string>;
  dialogId?: string;
  trackSize = widthTracker;
  @tracked appWidth!: number;

  @action popState(/*event*/) {
    if (window.location.href.localeCompare(this.currentLoadedUrl) !== 0) {
      // // Reload Page with new Query String Values
      location.reload();
    }
  }

  @action setAppWidth(width: number) {
    this.appWidth = width;
  }

  setupStudioLinks() {
    this.siteUrl = this.siteconfig['SiteUrl'] as string;
    this.precisionStudioMenuLinks = {
      PrecisionStudioUrl: this.siteconfig['PrecisionStudioUrl'] as string,
      FilterWizardUrl: this.siteconfig['FilterWizardUrl'] as string,
      AdcDriverUrl: this.siteconfig['AdcDriverUrl'] as string,
      PhotodiodeUrl: this.siteconfig['PhotodiodeUrl'] as string,
      DiamondUrl: this.siteconfig['DiamondUrl'] as string,
      SimDDSUrl: this.siteconfig['SimDDSUrl'] as string,
      VirtualEvalUrl: this.siteconfig['VirtualEvalUrl'] as string,
      DACErrorCalculatorUrl: this.siteconfig['DACErrorCalculatorUrl'] as string,
      NoiseUrl: this.siteconfig['NoiseUrl'] as string,
      SignalChainDesignerUrl: this.siteconfig['SignalChainDesignerUrl'] as string,
    };
  }

  disableBodyScroll() {
    setTimeout(function () {
      window.scrollTo(0, 0);
    }, 50);
    document.querySelector('body')?.classList.add('disable-scroll');
  }

  enableBodyScroll() {
    document.querySelector('body')?.classList.remove('disable-scroll');
  }

  get productPageURL() {
    const result = (this.siteconfig['ProductPageUrl'] as string)
      .replace('{0}', this.siteconfig.AdiLocale)
      .replace('{part}', this.circuit.partData ? this.circuit.partData.name : '');

    return result + this.circuit.nonDDSQueryString;
  }
  get dataSheetURL() {
    const datasheetLocale = 'en'; //always English
    const result = (this.siteconfig['DataSheetUrl'] as string)
      .replace('{0}', datasheetLocale)
      .replace('{part}', this.circuit.partData ? this.circuit.partData.name : '');

    return result + this.circuit.nonDDSQueryString;
  }

  @action consentReady() {
    document.querySelector('.aditools-consent-dialog')?.classList.add('cookie-consent-inserted');
  }

  @action selectedPartChanged() {
    this.analytics.setWebEvent(
      'SimDDS',
      'selectPart',
      'partID: ' + this.circuit.partData ? this.circuit.partData?.name : '',
    );
  }
  @action tabChanged(tab: number) {
    set(this, 'selectedTab', tab);
    set(this, 'activeTab', TAB_NAME_LIST[this.selectedTab]);

    switch (tab) {
      case K_OUTPUT_TAB:
        this.analytics.setWebEvent('SimDDS', 'selectTab', 'TabName: Output Signal');
        break;
      case K_TABLE_TAB:
        this.analytics.setWebEvent('SimDDS', 'selectTab', 'TabName: Spur Table');
        break;
      case K_DEBUG_TAB:
        break;
    }
  }
  @action insertedSharePopup() {
    const dialog = document.querySelector('#share_dialog') as HTMLDialogElement;
    dialog.showModal();
  }
  @action closeShareDialog(doClose: () => void, event: MouseEvent) {
    const dialog = document.querySelector('#share_dialog') as HTMLDialogElement;
    const popup = document.querySelector('#share-popup') as HTMLElement;
    const target = event.target as HTMLInputElement;
    if (!popup.contains(target)) {
      dialog.close();
      doClose();
    }
  }
  @action openDialog(dialogID: string) {
    const id = dialogID.replace('_', '-');
    set(this, 'dialogId', dialogID);

    if (dialogID === 'feedback_survey') {
      this.analytics.setWebEvent('SimDDS', 'openFeedback', 'Feedback URL: ' + this.siteconfig['ApiUrl']);
    } else {
      this.analytics.setWebEvent('SimDDS', 'openHelp', 'HelpDialog: ' + id);
    }

    this.disableBodyScroll();
  }
  @action closeDialog() {
    set(this, 'dialogId', '');
    this.enableBodyScroll();
  }
  @action postShareAnalytics() {
    this.analytics.setWebEvent('SimDDS', 'openShare', 'shareURL: ' + window.location.href);
  }
  @action postDialogAnalytics(dialogID: string) {
    if (dialogID === 'feedback_survey') {
      this.analytics.setWebEvent('SimDDS', 'openFeedback', 'Feedback URL: ' + this.siteconfig['ApiUrl']);
    } else {
      const id = dialogID.replace('_', '-');
      this.analytics.setWebEvent('SimDDS', 'openHelp', 'HelpDialog: ' + id);
    }
  }
  @action postProdPageAnalytics() {
    this.analytics.setWebEvent(
      'SimDDS',
      'openProdPage',
      'partID: ' + this.circuit.partData ? this.circuit.partData?.name : '',
    );
  }
  @action postDataSheetAnalytics() {
    this.analytics.setWebEvent(
      'SimDDS',
      'openDataSheet',
      'partID: ' + this.circuit.partData ? this.circuit.partData?.name : '',
    );
  }

  @action giveFeedback(model: FeedbackModel) {
    return Promise.resolve()
      .then(() => {
        this.systemDialogFunc('ON', 'applicationBusy');
        return delay(250).then(() => {
          return;
        });
      })
      .then(() => {
        return this.feedback.deliverFeedback(model.name, model.email, model.feedback);
      })
      .then((result) => {
        this.systemDialogFunc('OFF', '');
        if (result.status === 0) {
          this.showNotification('dialog.feedback_survey.header', 'dialog.feedback_survey.feedbackDialogOkMessage');
        } else {
          this.showNotification('dialog.feedback_survey.header', 'dialog.feedback_survey.feedbackDialogNgMessage');
        }
      })
      .catch((error) => {
        this.systemDialogFunc('OFF', '');
        this.showNotification('dialog.feedback_survey.header', 'dialog.feedback_survey.feedbackDialogNgMessage');
        this.logger.log('Error:' + error.toString());
      });
  }

  showNotification(title: string, message: string, msgparams?: { htmlSafe?: boolean }) {
    let adjparams = msgparams;
    if (adjparams) {
      adjparams.htmlSafe = true;
    } else {
      adjparams = { htmlSafe: true };
    }
    set(this.notificationDialog, 'title', this.intl.t(title, { htmlSafe: true }));
    set(this.notificationDialog, 'message', this.intl.t(message, adjparams));
    set(this.notificationDialog, 'enabled', true);
  }

  @action closeNotification() {
    set(this.notificationDialog, 'enabled', false);
    set(this.notificationDialog, 'title', '');
    set(this.notificationDialog, 'message', '');
  }

  systemDialogFunc(status: string, message: string) {
    switch (status) {
      case 'ON':
        set(this.systemDialog, 'enabled', true);
        set(this.systemDialog, 'message', this.intl.t(`dialog.system.${message}`));
        break;
      case 'OFF':
        set(this.systemDialog, 'enabled', false);
        set(this.systemDialog, 'message', '');
        break;
      case 'NONE':
        break;
      default:
        break;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  @action closeConsentDialog() {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  @action closeShare() {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  @action openErrorDialog() {}
}

declare module '@ember/controller' {
  interface Registry {
    index: IndexController;
  }
}
